import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { tokens } from "../../../../../theme";

import Text from "../../../../theme/text/Text";
import Subheading from "../../../../theme/text/Subheading";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { WarehouseInboundLineProductCard } from '../../../../global/cards/inbounds/InboundLineProductCard';
import ShDataGrid2 from '../../../../theme/datagrid2/ShDataGrid2';
import ShTextSkeleton from '../../../../theme/skeleton/ShTextSkeleton';
import SidepanelLoader from '../../../../theme/sidepanel2/SidepanelLoader';
import { useGetBolreplenishmentLinesQuery } from '../../../../../newapi/webshop/bolreplenishmentSlice';
import { useGetLogsQuery } from '../../../../../newapi/global/logsSlice';
import { getTimeLineSettings } from '../../SpOrderDetails/TimeLineMap';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { ENV_TYPES } from '../../../Sidebar';

const SpStockDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpStockDetails/SPStockDetails"));

const TimeLine = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isSmallScreen = useMediaQuery('(max-width: 47.9375em)');

    const logItems = isSmallScreen
      ? props.logData?.data || []           // small screen: oldest first
      : [...(props.logData?.data || [])].reverse(); // large screen: newest first

    return (
        <Box
            sx={{
                mr:3,
                mt:3,
                flex:1,
                p:2,
                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[50],
                borderRadius:4,
                "@media screen and (max-width: 47.9375em)" : {
                    display:'flex',
                    overflow:"scroll",
                    ml:3,
                    background:"none",
                    p:0,
                    mt:1.5,
                }
            }}
        >
            <Subheading>{t("timeline")}</Subheading>
            {logItems.map((logline, idx) => {
                const { comp: Component, check } = getTimeLineSettings(logline.action?.toLowerCase(),theme.palette.mode);
                return  (
                    <Box
                        key={"timeline"+idx}
                        sx={{
                            mt:3,
                            display:"flex",
                            gap:1.5,
                            position:"relative",
                            "&:before" : {
                                content: '""',
                                display:"block",
                                width:2,
                                height:idx < (props.logData.data.length -1) ? 1 : 0,
                                position:"absolute",
                                left:11,
                                background:colors[check][400],
                                top:24,
                                "@media screen and (max-width: 47.9375em)" : {
                                    top:11,
                                    height:2,
                                    width:idx < (props.logData.data.length -1) ? 1 : 0,
                                    left:24,
                                }
                            },
                            "@media screen and (max-width: 47.9375em)" : {
                                flexDirection:'column',
                                pr:idx < (props.logData.data.length -1) ? 5 : 0,
                                mt:0,
                                gap:1,
                            }
                        }}
                    >
                        <Box
                            sx={{
                                flexShrink:0,
                                height:24,
                                width:24,
                                background:colors[check][400],
                                borderRadius:3,
                                color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                display:"flex",
                                alignItems:'center',
                                justifyContent:"center",
                                fontWeight:"bold"
                            }}
                        >
                            <CheckOutlinedIcon fontSize="small"/>
                        </Box>
                        <Component
                            key={logline.type+idx}
                            {...logline}
                            isRelation={props.isRelation}
                        />
                    </Box>
                );
            })}

        </Box>
    )
}


const gridOptions = {
    export:true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    hide:true,
    getRowHeight: () => 'auto',
    sx: {
        '& .MuiDataGrid-cell': {
            maxHeight: 'none !important',
            minHeight: 'auto !important',
        },
        '& .MuiDataGrid-row': {
            maxHeight: 'none !important',
        }
    },
    filter : {
        name: "productsFilter",
        selectedFilter : null,
        groups : [
            {
                conditionExtensionType : 0,
                rules : [
                ]
            }
        ]
    }
};

const Products = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const [isExtended, setIsExtended] = useState(false);
    const [spStockLoading, setSpStockLoading] = useState();

    const [productId, setProductId] = useState();
    const productIdRef = useRef(productId);

    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === ENV_TYPES[200];

    const {data: logData} = useGetLogsQuery({
        args: {
            page: 1,
            size: 100,
            body: {
                "type": "BolReplenishment",
                "externalId": props.data?.id
            }
        }
    },{
        skip: !props.data || !props.data?.id
    })

    const getProductDetails = (params) => {
        if (!params) return;

        const newProductId = parseInt(params.row.product?.id);
        if(newProductId === null || isNaN(newProductId)) {
            return;
        }
        // Only update state if the productId has actually changed
        if (productIdRef.current !== newProductId) {
            if(!spStockLoading){
                setSpStockLoading(true);
            }
            setProductId(newProductId);
            productIdRef.current = newProductId; // Update the ref to the new value
        }

            setIsExtended(true);
    };


    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions:gridOptions,
            afterHeader: (
                <Box>
                    <TimeLine
                        logData={logData}
                        isRelation={isRelation}
                    />
                </Box>
            )
        });

        return () => {
            // console.log("unmounting warehouse");
        }
    },[props.config, logData, isRelation]);
    

    const columns = [
        {
            field: "product",
            headerName: t("product"),
            flex: 3,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return (
                        <Box sx={{py: 1.5, width: 1}}>
                            <ShTextSkeleton palette={theme.palette.mode === "dark" ? "pastel" : "normal"} variant={theme.palette.mode === "dark" ? "blue" : "primary"} />
                            <ShTextSkeleton sx={{mt: 0.75}} />
                        </Box>
                    )
                }
                const sku = params.row.product?.sku || "-";
                const description = params.row.product?.description || "-";
                return (
                    <Box sx={{py: 1.5, width: 1}}>
                        <Text clickable semibold variant={theme.palette.mode === "dark" ? "blue" : "primary"}>{sku}</Text>
                        <Box sx={{mt: 0.75}}>
                            <Text>{description}</Text>
                        </Box>
                    </Box>
                );
            }
        },
        {
            field: "amount",
            headerName: t("amount"),
            flex: 1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return (
                        <Box sx={{py: 1.5, width: 1}}>
                            <ShTextSkeleton/>
                        </Box>
                    )
                }
                return (
                    <Box sx={{py: 1.5, width: 1, display: 'flex', alignItems: 'center'}}>
                        <Text semibold>{params.value}</Text>
                    </Box>
                );
            }
        },
        {
            field: "quantities",
            headerName: t("quantities"),
            flex: 1.5,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return (
                        <Box sx={{py: 1.5, width: 1}}>
                            <ShTextSkeleton />
                        </Box>
                    )
                }
                const received = params.row.quantityReceived ?? 0;
                const announced = params.row.quantityAnnounced ?? 0;
                return (
                    <Box sx={{py: 1.5, width: 1, display: 'flex', alignItems: 'center'}}>
                        <Text semibold>{received} / {announced}</Text>
                    </Box>
                );
            }
        },
        {
            field: "inProgress",
            headerName: t("qty_in_progress"),
            flex: 1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return (
                        <Box sx={{py: 1.5, width: 1}}>
                            <ShTextSkeleton/>
                        </Box>
                    )
                }
                return (
                    <Box sx={{py: 1.5, width: 1, display: 'flex', alignItems: 'center'}}>
                        <Text semibold>{params.row.quantityInProgress ?? 0}</Text>
                    </Box>
                );
            }
        },
        {
            field: "gradedQuantities",
            headerName: t("graded_regular"),
            flex: 2,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return (
                        <Box sx={{py: 1.5, width: 1}}>
                            <ShTextSkeleton />
                            <ShTextSkeleton sx={{mt: 1}} />
                        </Box>
                    )
                }
                return (
                    <Box sx={{py: 1.5, width: 1}}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1}}>
                            <Text light>{t("qty_graded")}:</Text>
                            <Text semibold>{params.row.quantityWithGradedState ?? 0}</Text>
                        </Box>
                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                            <Text light>{t("qty_regular")}:</Text>
                            <Text semibold>{params.row.quantityWithRegularState ?? 0}</Text>
                        </Box>
                    </Box>
                );
            }
        },
        {
            field: "phoneView",
            headerName: "phoneView",
            cellClassName: "phone-column--cell",
            renderCell: (params) => {
                return (
                    <WarehouseInboundLineProductCard skeleton={params.row.skeleton ?? false} {...params.row} onClick={() => getProductDetails(params)}/>
                )
            }
        }
    ];

    return (
        <>
            <Box
                sx={{
                    height:1,
                    display:"flex",
                    "@media screen and (max-width: 47.9375em)" : {
                        flexDirection:"column"
                    }
                }}
            >
                <Box
                    sx={{
                        flex:2,
                        "@media screen and (max-width: 47.9375em)" : {
                            width:1,
                        }
                    }}
                >
                    {props.data && props.data.id &&
                        <Box sx={{px: 3, pt: 3}}>
                            <ShDataGrid2 key={theme.palette.mode}
                                itemId={props.data?.id}
                                content={useGetBolreplenishmentLinesQuery}
                                title={t("summary")}
                                gridOptions={gridOptions}
                                isSidepanelGrid
                                columns={columns}
                                onRowClick={getProductDetails}
                                sortModel={{field: columns[0].field,type:"DESC"}}
                            />
                        </Box>
                    }
                </Box>

                <Box
                    sx={{
                        flex:1,
                        "@media screen and (max-width: 47.9375em)" : {
                            display:'none',
                        }
                    }}
                >
                    <TimeLine
                        logData={logData}
                        isRelation={isRelation}
                    />
                </Box>
            </Box>
            {spStockLoading &&
                <SpStockDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    productId={productId}
                />
            }
        </>
      );

}

export default Products;